import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Amplify } from 'aws-amplify';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { EventConfigData } from 'src/app/@data-services/event-config/event-config.data-model';
import { ResolvedUser } from 'src/app/@data-services/user-auth/user-auth.data-model';
import { EventConfigService } from 'src/app/@services/event-config.service';
import { MixpanelService } from 'src/app/@services/mixpanel.service';
import { ReportsEventRouteSlugService } from 'src/app/@services/reports-event-route-slug.service';
import { UserAuthService } from 'src/app/@services/user-auth.service';
import { AuthStateService } from 'src/app/@stores/auth-state.service';
import { EventConfigStateService } from 'src/app/@stores/event-config-state.service';
import {
  buildEventReturnUrl,
  eventScopedCommands,
  isHubPath,
} from 'src/app/@utils/reports-navigation';
import { applyEuEnvironmentForEvent } from '../../config/eu-event-routing.config';
import { AUTH_ROUTES } from '../constants/auth-constants';
import { AuthSessionService } from '../services/auth-session';

const INTERNAL_EMAIL_DOMAIN = 'rozie.ai';

export const eventAuthGuard: CanActivateFn = (
  route,
  state
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const sessionService = inject(AuthSessionService);
  const eventConfigService = inject(EventConfigService);
  const eventConfigStateService = inject(EventConfigStateService);
  const userAuthService = inject(UserAuthService);
  const authStateService = inject(AuthStateService);
  const mixpanelService = inject(MixpanelService);
  const reportsEventSlug = inject(ReportsEventRouteSlugService);

  const eventName =
    reportsEventSlug.effectiveSlugWithPersistence(route) ||
    route.paramMap.get('eventName') ||
    '';
  const token = route.queryParamMap.get('token');

  // Override API URL, key, and Cognito pool for EU-routed events (restore defaults otherwise)
  const endpoints = applyEuEnvironmentForEvent(eventName);
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: endpoints.userPoolId,
        userPoolClientId: endpoints.userPoolClientId,
      },
    },
  });

  // Hub visits keep their path so login uses the hub theme and returns there.
  // Every other event route still returns to the event home.
  const requestedPath = state.url.split(/[?#]/)[0];
  const returnUrl = isHubPath(requestedPath)
    ? requestedPath
    : buildEventReturnUrl(eventName);

  const cachedConfig = eventConfigStateService.$eventConfig();
  const isSameEvent =
    cachedConfig &&
    cachedConfig.EventIdentifier?.toLowerCase() === eventName.toLowerCase();

  // When switching to a different event, force a full logout to clear
  // all state (auth, Mixpanel identity, registration, localStorage).
  // This prevents any identity or analytics from the previous event
  // leaking into the new event's session.
  if (!isSameEvent && cachedConfig) {
    userAuthService.logout();
  }

  if (isSameEvent) {
    return handleAuth(
      cachedConfig,
      eventName,
      token,
      sessionService,
      userAuthService,
      authStateService,
      mixpanelService,
      router,
      returnUrl
    );
  }

  // Use EventConfigService so non-prod can fall back from
  // `dev-{slug}.synopsis.rozie.ai` → EventIdentifier when Domain was saved
  // without the `dev-` prefix (common on develop.roziesynopsis.com).
  return eventConfigService.loadEventConfig(eventName, '').pipe(
    take(1),
    switchMap((config) => {
      if (config) {
        return handleAuth(
          config,
          eventName,
          token,
          sessionService,
          userAuthService,
          authStateService,
          mixpanelService,
          router,
          returnUrl
        );
      }
      return of(true);
    }),
    catchError(() => of(true))
  );
};

function handleAuth(
  config: EventConfigData | undefined,
  eventName: string,
  token: string | null,
  sessionService: AuthSessionService,
  userAuthService: UserAuthService,
  authStateService: AuthStateService,
  mixpanelService: MixpanelService,
  router: Router,
  returnUrl: string
): Observable<boolean | UrlTree> {
  const features = config?.Features;
  const knowledgeHubConfig = config?.KnowledgeHubConfig;

  // Determine if authentication is required
  // KnowledgeHubConfig takes precedence if present
  let requiresAuth = false;
  if (knowledgeHubConfig) {
    requiresAuth = knowledgeHubConfig.RequiredLogin ?? false;
  } else {
    requiresAuth = features?.RequireAuthentication ?? false;
  }

  if (!requiresAuth) {
    // No authentication required — enable anonymous tracking so
    // events are sent immediately without waiting for user identification
    mixpanelService.enableAnonymousTracking();
    return of(true);
  }

  const isUsernamePasswordLogin =
    knowledgeHubConfig?.LoginType === 'USERNAME/PASSWORD' ||
    (knowledgeHubConfig as { loginType?: string } | undefined)?.loginType ===
      'USERNAME/PASSWORD';

  // Shared username/password gate — skip Cognito OTP path entirely
  if (isUsernamePasswordLogin) {
    if (userAuthService.checkExistingAuth(eventName)) {
      const status = authStateService.$status();
      if (
        status === 'blocked' ||
        status === 'no_entitlement' ||
        status === 'error'
      ) {
        return of(router.createUrlTree(['/event', eventName, 'blocked']));
      }
      return of(true);
    }

    return of(
      router.createUrlTree([AUTH_ROUTES.LOGIN], {
        queryParams: { returnUrl, auth: 'password' },
      })
    );
  }

  // Path 1: Token-based direct access (e.g. ?token=abc from email link)
  if (token && (features?.AllowTokenAuth ?? true)) {
    return userAuthService.authenticateWithToken(eventName, token).pipe(
      take(1),
      map((result) => {
        if (result.success) {
          return true;
        }

        return redirectForAuthStatus(
          authStateService,
          router,
          eventName,
          returnUrl
        );
      }),
      catchError(() =>
        of(
          router.createUrlTree(
            eventScopedCommands(router, eventName, 'blocked')
          )
        )
      )
    );
  }

  // Path 2: Check if user already has a valid resolved identity for this event
  if (userAuthService.checkExistingAuth(eventName)) {
    // Verify entitlement status
    const status = authStateService.$status();
    if (
      status === 'blocked' ||
      status === 'no_entitlement' ||
      status === 'error'
    ) {
      return of(
        router.createUrlTree(eventScopedCommands(router, eventName, 'blocked'))
      );
    }
    return of(true);
  }

  // Path 3: Check Cognito session — user may be OTP-authenticated but identity not yet resolved
  return sessionService.checkSession$().pipe(
    take(1),
    switchMap((session) => {
      if (!session.isAuthenticated) {
        // Not authenticated at all — redirect to login
        return of(
          router.createUrlTree([AUTH_ROUTES.LOGIN], {
            queryParams: { returnUrl },
          })
        );
      }

      // Cognito session valid — resolve identity against backend
      return sessionService.getUserEmail$().pipe(
        take(1),
        switchMap((email) => {
          if (!email) {
            return of(
              router.createUrlTree([AUTH_ROUTES.LOGIN], {
                queryParams: { returnUrl },
              })
            );
          }

          // Internal @rozie.ai users: skip attending-level validation,
          // OTP verification is sufficient
          const emailDomain = email.split('@')[1]?.toLowerCase();
          if (emailDomain === INTERNAL_EMAIL_DOMAIN) {
            const internalUser: ResolvedUser = {
              userId: email,
              registrationId: 'internal',
              email,
              entitlements: [],
            };
            authStateService.setEventNameIdentifier(eventName);
            authStateService.setUser(internalUser);
            mixpanelService.setUserInfo(email);
            return of(true);
          }

          return userAuthService.authenticateWithEmail(eventName, email).pipe(
            take(1),
            map((result) => {
              if (result.success) {
                return true;
              }

              return redirectForAuthStatus(
                authStateService,
                router,
                eventName,
                returnUrl
              );
            }),
            catchError((error) => {
              // Distinguish auth failures from network/server errors
              if (
                error instanceof HttpErrorResponse &&
                (error.status === 401 ||
                  error.status === 403 ||
                  error.status === 404)
              ) {
                // Auth rejection — redirect to blocked page
                return of(
                  router.createUrlTree(
                    eventScopedCommands(router, eventName, 'blocked')
                  )
                );
              }
              // Network/server error (5xx, status 0) — fail closed, redirect to login
              console.error(
                '[eventAuthGuard] validateUser server error; redirecting to login',
                error
              );
              return of(
                router.createUrlTree([AUTH_ROUTES.LOGIN], {
                  queryParams: { returnUrl },
                })
              );
            })
          );
        })
      );
    }),
    catchError(() =>
      of(
        router.createUrlTree([AUTH_ROUTES.LOGIN], {
          queryParams: { returnUrl },
        })
      )
    )
  );
}

function redirectForAuthStatus(
  authStateService: AuthStateService,
  router: Router,
  eventName: string,
  returnUrl: string
): UrlTree {
  const status = authStateService.$status();

  if (
    status === 'blocked' ||
    status === 'no_entitlement' ||
    status === 'unresolved' ||
    status === 'error'
  ) {
    return router.createUrlTree(
      eventScopedCommands(router, eventName, 'blocked')
    );
  }

  // Fallback — send to login
  return router.createUrlTree([AUTH_ROUTES.LOGIN], {
    queryParams: { returnUrl },
  });
}
